/**
 * PricingSection Component
 * 
 * Displays pricing plans from the active pricing grid.
 * Handles A/B testing automatically via usePricingGrid hook.
 * Supports safe/dark mode styling.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { 
  Check, 
  ArrowRight, 
  Users, 
  Crown, 
  Loader2,
  AlertCircle,
  Sparkles
} from 'lucide-react';
import { usePricingGrid } from '../../hooks/usePricingGrid';

/**
 * Format price from cents to display string
 */
const formatPrice = (priceCents, billingCycle) => {
  if (priceCents === 0 || priceCents === null || priceCents === undefined) {
    return { amount: '$0', period: 'forever' };
  }
  
  const amount = `$${(priceCents / 100).toFixed(0)}`;
  let period = '/PM/month';  // Price is per Product Manager per month
  
  if (billingCycle === 'yearly') {
    period = '/PM/year';
  } else if (billingCycle === 'seat_monthly') {
    period = '/seat/mo';
  } else if (billingCycle === 'seat_yearly') {
    period = '/seat/yr';
  }
  
  return { amount, period };
};

/**
 * Single pricing card component
 */
const PricingCard = ({ plan, safeMode, isHighlighted, onSelect, isOAuthFlow }) => {
  const navigate = useNavigate();
  const price = formatPrice(plan.price_cents, plan.billing_cycle);
  const display = plan.display || {};
  const restrictions = plan.restrictions || {};
  
  // Build features list from restrictions and display
  const features = [];
  
  // First, check if there are feature_bullets in display (preferred)
  if (display.feature_bullets && Array.isArray(display.feature_bullets) && display.feature_bullets.length > 0) {
    features.push(...display.feature_bullets);
  } else {
    // Fallback: build from restrictions
    // Add seat info
    if (restrictions.max_paid_seats !== undefined && restrictions.max_paid_seats !== null) {
      if (restrictions.max_paid_seats === -1) {
        features.push('Unlimited PM seats');
      } else if (restrictions.max_paid_seats === 1) {
        features.push('1 Owner seat');
      } else if (restrictions.max_paid_seats > 0) {
        features.push(`${restrictions.max_paid_seats} PM seat${restrictions.max_paid_seats > 1 ? 's' : ''}`);
      }
    }
    
    if (restrictions.max_collaborator_seats !== undefined && restrictions.max_collaborator_seats !== null) {
      if (restrictions.max_collaborator_seats === -1) {
        features.push('Unlimited Collaborators');
      } else if (restrictions.max_collaborator_seats > 0) {
        features.push(`${restrictions.max_collaborator_seats} Collaborator seats`);
      }
    }
    
    if (restrictions.max_products !== undefined && restrictions.max_products !== null) {
      if (restrictions.max_products === -1) {
        features.push('Unlimited products');
      } else if (restrictions.max_products > 0) {
        features.push(`Up to ${restrictions.max_products} products`);
      }
    }
    
    if (restrictions.max_features !== undefined && restrictions.max_features !== null) {
      if (restrictions.max_features === -1) {
        features.push('Unlimited features');
      } else if (restrictions.max_features > 0) {
        features.push(`Up to ${restrictions.max_features} active features`);
      }
    }
    
    // Add priority support
    if (restrictions.priority_support) {
      features.push('Priority support');
    }
  }
  
  // Trial info
  const trialDays = plan.trial_days || 0;
  
  const handleClick = () => {
    if (plan.plan_type === 'inquire' && plan.inquire_url) {
      window.open(plan.inquire_url, '_blank');
    } else if (isOAuthFlow && onSelect) {
      // OAuth signup flow - call the handler directly instead of navigating
      onSelect(plan);
    } else {
      // Normal flow - navigate to signup page
      navigate('/signup', { 
        state: { 
          selectedPlanId: plan.plan_id,
          planLabel: plan.label 
        } 
      });
    }
  };
  
  return (
    <Card className={`p-8 relative transition-all duration-500 h-full flex flex-col ${
      isHighlighted
        ? safeMode
          ? 'bg-white border-2 border-blue-500 shadow-xl z-10'
          : 'bg-gradient-to-br from-purple-900/30 to-pink-900/30 border-2 border-purple-500 shadow-xl shadow-purple-500/20 z-10'
        : safeMode
          ? 'bg-white border-gray-200 hover:border-blue-300'
          : 'bg-[#0a0a0a] border-white/10 hover:border-purple-500/30'
    }`} style={isHighlighted ? { transform: 'scale(1.05)' } : {}}>
      {/* Popular badge */}
      {isHighlighted && (
        <Badge className={`absolute -top-3 left-1/2 transform -translate-x-1/2 transition-all duration-500 ${
          safeMode
            ? 'bg-gradient-to-r from-blue-600 to-indigo-600 text-white border-0'
            : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white border-0'
        }`}>
          <Crown className="w-3 h-3 mr-1" />
          {display.badge || 'Most Popular'}
        </Badge>
      )}
      
      {/* Plan name */}
      <h3 className={`text-2xl font-bold mb-2 transition-colors duration-500 ${
        safeMode ? 'text-gray-900' : 'text-white'
      }`}>
        {plan.label}
      </h3>
      
      {/* Price */}
      <div className="mb-4">
        {plan.plan_type === 'inquire' ? (
          <span className={`text-3xl font-bold transition-colors duration-500 ${
            safeMode ? 'text-gray-900' : 'text-white'
          }`}>
            Custom Pricing
          </span>
        ) : (
          <>
            <span className={`text-5xl font-bold transition-colors duration-500 ${
              safeMode ? 'text-gray-900' : 'text-white'
            }`}>
              {price.amount}
            </span>
            <span className={`transition-colors duration-500 ${
              safeMode ? 'text-gray-600' : 'text-gray-400'
            }`}>
              {price.period}
            </span>
          </>
        )}
      </div>
      
      {/* Description */}
      {(display.description || display.tagline) && (
        <p className={`mb-6 transition-colors duration-500 ${
          safeMode ? 'text-gray-600' : 'text-gray-400'
        }`}>
          {display.description || display.tagline}
        </p>
      )}
      
      {/* CTA Button */}
      <Button 
        className={`w-full mb-6 transition-all duration-500 ${
          isHighlighted
            ? safeMode
              ? 'bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700'
              : 'bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-400 hover:to-pink-400 shadow-lg shadow-purple-500/30'
            : safeMode
              ? 'bg-gray-900 hover:bg-gray-800'
              : 'bg-white text-black hover:bg-gray-200'
        }`}
        onClick={handleClick}
      >
        {plan.plan_type === 'inquire' 
          ? 'Contact Sales' 
          : trialDays > 0 
            ? `Start ${trialDays}-Day Trial`
            : display.cta || display.cta_text || 'Get Started'
        }
        <ArrowRight className="w-4 h-4 ml-2" />
      </Button>
      
      {/* Features list */}
      <ul className="space-y-3">
        {features.map((feature, j) => (
          <li key={j} className={`flex items-center gap-2 transition-colors duration-500 ${
            safeMode ? 'text-gray-700' : 'text-gray-300'
          }`}>
            <Check className={`w-5 h-5 shrink-0 ${
              safeMode ? 'text-green-500' : 'text-purple-400'
            }`} />
            <span>{feature}</span>
          </li>
        ))}
      </ul>
      
      {/* Team note */}
      {(display.team_note || display.footer_text) && (
        <div className={`mt-6 pt-4 border-t transition-colors duration-500 ${
          isHighlighted
            ? safeMode ? 'border-blue-200' : 'border-purple-500/30'
            : safeMode ? 'border-gray-200' : 'border-white/10'
        }`}>
          <p className={`text-sm font-medium flex items-center gap-2 ${
            isHighlighted
              ? safeMode ? 'text-blue-600' : 'text-pink-400'
              : safeMode ? 'text-blue-600' : 'text-purple-400'
          }`}>
            <Users className="w-4 h-4" />
            {display.team_note || display.footer_text}
          </p>
        </div>
      )}
    </Card>
  );
};

/**
 * Main PricingSection component
 */
const PricingSection = ({ safeMode = true, onPlanSelect }) => {
  const navigate = useNavigate();
  const { 
    loading, 
    error, 
    plans, 
    assignedVariant,
    getFreePlan,
    getPaidPlans,
    getEnterprisePlan
  } = usePricingGrid();
  
  if (loading) {
    return (
      <section className={`py-24 px-6 transition-colors duration-500 ${
        safeMode ? 'bg-white' : 'bg-[#050505]'
      }`}>
        <div className="max-w-5xl mx-auto text-center">
          <Loader2 className={`w-8 h-8 animate-spin mx-auto ${
            safeMode ? 'text-blue-600' : 'text-purple-500'
          }`} />
          <p className={`mt-4 ${safeMode ? 'text-gray-600' : 'text-gray-400'}`}>
            Loading pricing...
          </p>
        </div>
      </section>
    );
  }
  
  if (error || plans.length === 0) {
    // Fallback to static pricing if API fails
    return null; // Let parent component handle fallback
  }
  
  const freePlan = getFreePlan();
  const paidPlans = getPaidPlans();
  const enterprisePlan = getEnterprisePlan();
  
  // Determine which plan to highlight based on show_popular_badge or first paid plan
  const getHighlightedPlanId = () => {
    // First check if any plan has show_popular_badge
    const allPlans = [freePlan, ...paidPlans, enterprisePlan].filter(Boolean);
    const badgedPlan = allPlans.find(p => p.display?.show_popular_badge);
    if (badgedPlan) return badgedPlan.plan_id;
    // Fallback to first paid plan
    return paidPlans[0]?.plan_id;
  };
  const highlightedPlanId = getHighlightedPlanId();
  
  // Combine plans in display order: free, paid plans, enterprise
  const displayPlans = [freePlan, ...paidPlans, enterprisePlan].filter(Boolean);
  
  // Determine if this is an OAuth signup flow
  const isOAuthFlow = !!onPlanSelect;
  
  return (
    <section className={`py-24 px-6 transition-colors duration-500 ${
      safeMode ? 'bg-white' : 'bg-[#050505]'
    }`}>
      <div className="max-w-[1200px] mx-auto">
        {/* Section header */}
        <div className="text-center mb-16">
          <h2 className={`text-3xl md:text-4xl font-semibold tracking-tight mb-4 transition-colors duration-500 ${
            safeMode ? 'text-gray-900' : 'text-white'
          }`}>
            Transparent Pricing
          </h2>
          <p className={`transition-colors duration-500 max-w-2xl mx-auto ${
            safeMode ? 'text-gray-600' : 'text-gray-400'
          }`}>
            Try it free and upgrade when you need more power. You only pay for Executive and Product Manager seats - the 
            designers, developers, and testers you collaborate with to ship great products are always free. Cancel anytime - no long-term contracts.
          </p>
        </div>
        
        {/* Pricing cards grid */}
        <div className={`grid gap-8 items-stretch py-8 ${
          displayPlans.length === 2 
            ? 'md:grid-cols-2 max-w-4xl mx-auto' 
            : displayPlans.length === 3
              ? 'md:grid-cols-3 max-w-5xl mx-auto'
              : 'md:grid-cols-2 lg:grid-cols-4'
        }`}>
          {displayPlans.map((plan) => (
            <PricingCard
              key={plan.plan_id}
              plan={plan}
              safeMode={safeMode}
              isHighlighted={plan.plan_id === highlightedPlanId}
              onSelect={onPlanSelect}
              isOAuthFlow={isOAuthFlow}
            />
          ))}
        </div>
        
        {/* Free Explorer CTA — prominent, above enterprise */}
        <div className={`text-center mt-12 p-8 rounded-xl ${
          safeMode ? 'bg-amber-50 border border-amber-200' : 'bg-amber-500/5 border border-amber-500/20'
        }`}>
          <h3 className={`text-xl font-semibold mb-2 ${
            safeMode ? 'text-gray-900' : 'text-white'
          }`}>
            Just exploring an idea?
          </h3>
          <p className={`mb-4 max-w-xl mx-auto ${
            safeMode ? 'text-gray-600' : 'text-gray-400'
          }`}>
            No credit card needed. Get started with AI-powered product planning in seconds.
          </p>
          <Button
            className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-6"
            onClick={() => navigate('/signup/explorer')}
            data-testid="explore-free-cta"
          >
            <Sparkles className="w-4 h-4 mr-2" />
            Start building for free
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </div>

        {/* Enterprise CTA if no enterprise plan in grid */}
        {!enterprisePlan && (
          <div className={`text-center mt-8 p-8 rounded-xl ${
            safeMode ? 'bg-gray-50' : 'bg-[#0a0a0a] border border-white/10'
          }`}>
            <h3 className={`text-xl font-semibold mb-2 ${
              safeMode ? 'text-gray-900' : 'text-white'
            }`}>
              Need More Enterprise Features?
            </h3>
            <p className={`mb-4 max-w-xl mx-auto ${
              safeMode ? 'text-gray-600' : 'text-gray-400'
            }`}>
              We support Google, Microsoft, and email/password authentication out of the box. 
              For Enterprise, large PM teams, or agencies, we offer SSO/SAML integration, dedicated support, and custom configurations.
            </p>
            <Button 
              variant="outline" 
              className={safeMode ? '' : 'border-white/20 text-white hover:bg-white/5'}
              onClick={() => navigate('/contact-sales')}
            >
              Contact Sales
            </Button>
          </div>
        )}
      </div>
    </section>
  );
};

export default PricingSection;
